<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import Sheet from '../ui/Sheet.vue'
import Icon from '../ui/Icon.vue'
import Kbd from '../ui/Kbd.vue'
import { ariaCombo, isTyping } from '../../lib/shortcuts'
import BackupPanel from '../settings/BackupPanel.vue'
import CloudPanel from '../settings/CloudPanel.vue'
import SyncPanel from '../settings/SyncPanel.vue'
import LedgerStates from '../settings/LedgerStates.vue'
import LedgerPicker from '../settings/LedgerPicker.vue'
import { filesUsage } from '../../stores/attachments'
import { sizeLabel } from '../../lib/attachments'
import SuggestionsPanel from '../settings/SuggestionsPanel.vue'
import UsagePanel from '../settings/UsagePanel.vue'
import * as cloud from '../../stores/cloud'
import { mightBeSignedIn } from '../../stores/sync'
import { accountBalance, transactionCount } from '../../stores'
import { hydrate } from '../../stores'
import { state, closeSheet, toast } from '../../stores/state'
import { count } from '../../stores/usage'
import { LANGUAGES, errorText, language, setLanguage, t } from '../../stores/i18n'
import { setSetting } from '../../stores/settings'
import { eraseEverything, loadDemoData } from '../../stores/demo'
import { importDatabase } from '../../db'
import { looksLikeZip, unzipWithPassword } from '../../lib/securezip'
import { isLocked, unzip } from '../../lib/zip'
import { readBackup } from '../../stores/backup'
import { fileRows, restoreFiles } from '../../stores/attachments'
import { CURRENCIES, parseAmount, round2 } from '../../lib/money'
import { IMPORTERS } from '../../lib/importers'

const props = defineProps({ payload: { type: Object, default: () => ({}) } })

/**
 * Four tabs, down the left from `sm` and across the top on a phone, holding
 * everything Settings has — nothing here opens another sheet. A panel is
 * mounted the first time its tab is opened (Suggestions reads every row, the
 * cloud asks the server) and then kept (v-show), so a password half-typed for
 * a protected backup is still there after a look at another tab.
 * `payload.tab` opens one. A fifth, Usage, is there only for an admin (the
 * session says so), asked only of a browser that is or has been signed in.
 */
const ALL_TABS = [
  { id: 'general', icon: 'settings', label: 'settings.tab.general' },
  { id: 'suggestions', icon: 'spark', label: 'settings.tab.suggestions' },
  { id: 'sync', icon: 'repeat', label: 'settings.tab.sync' },
  { id: 'data', icon: 'folder', label: 'settings.tab.data' },
  { id: 'usage', icon: 'gauge', label: 'settings.tab.usage', admin: true },
]
const admin = ref(false)
const TABS = computed(() => ALL_TABS.filter((x) => !x.admin || admin.value))
onMounted(async () => {
  if (cloud.getMode() !== 'cloud' && !mightBeSignedIn()) return
  admin.value = !!(await cloud.me().catch(() => null))?.admin
  if (admin.value && props.payload?.tab === 'usage') tab.value = 'usage'
})
const tab = ref(TABS.value.some((x) => x.id === props.payload?.tab) ? props.payload.tab : 'general')
const opened = reactive(new Set([tab.value]))
watch(tab, (id) => opened.add(id))

/** What the attached files take — asked when Your data is opened. */
const filesUsed = ref(null)
watch(
  tab,
  async (id) => {
    if (id === 'data') filesUsed.value = await filesUsage().catch(() => null)
  },
  { immediate: true },
)

/**
 * 1–9 pick a tab by its place — from anywhere in the sheet but a field being
 * typed into, where a digit is a digit.
 */
function onNumber(event) {
  if (event.defaultPrevented || isTyping(event.target) || event.ctrlKey || event.metaKey || event.altKey) return
  const n = Number(event.key)
  if (!Number.isInteger(n) || n < 1 || n > TABS.value.length) return
  event.preventDefault()
  tab.value = TABS.value[n - 1].id
  nextTick(() => document.querySelector(`[data-settings-tab="${tab.value}"]`)?.focus())
}
onMounted(() => document.addEventListener('keydown', onNumber))
onBeforeUnmount(() => document.removeEventListener('keydown', onNumber))

/** Up and down (or left and right) move between tabs, as in any tab list. */
function onTabKey(event) {
  const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key]
  if (!step) return
  event.preventDefault()
  const at = TABS.value.findIndex((x) => x.id === tab.value)
  tab.value = TABS.value[(at + step + TABS.value.length) % TABS.value.length].id
  event.currentTarget.querySelector(`[data-settings-tab="${tab.value}"]`)?.focus()
}

const busy = ref('')
const confirmErase = ref(false)
const fileInput = ref(null)
/** A protected backup waits here until its password arrives. */
const locked = ref(null)
const password = ref('')
const restoreError = ref('')

const opening = computed({
  get: () => String(state.settings.openingBalance ?? 0),
  set: (v) => setSetting('openingBalance', parseAmount(v) || 0),
})
const balanceField = computed(() => String(round2(accountBalance.value)))

/**
 * Works the opening balance back from the balance the bank actually shows, so
 * the two agree without anyone doing the arithmetic. Committed on blur or
 * Enter rather than per keystroke — a half-typed number is not a balance.
 */
function matchBalance(event) {
  const target = parseAmount(event.target.value)
  if (Number.isFinite(target) && target !== accountBalance.value) {
    // The balance is the opening plus everything since, so the opening is the
    // target less everything since.
    const since = accountBalance.value - (Number(state.settings.openingBalance) || 0)
    setSetting('openingBalance', round2(target - since))
    toast(t('settings.balanceMatched'), 'good')
  }
  event.target.value = balanceField.value
}

/** Restores a backup: the ledger, then — from a zip — its files, put where this ledger keeps them. */
async function load({ ledger, files = [] }, kind = 'zip') {
  await importDatabase(ledger)
  count('restore', kind)
  await hydrate()
  const put = files.length ? await restoreFiles(files) : 0
  const named = (await fileRows()).length
  if (put < named) toast(t('settings.restoredMissing', { count: named - put }), 'warn')
  else toast(t('settings.restored'), 'good')
}

async function restore(event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) return
  // Each of these replaces or wipes the whole ledger, which in sync mode would do it on every device.
  if (state.sync.on) return toast(t('sync.refused'), 'warn')
  restoreError.value = ''
  busy.value = 'restore'
  try {
    const bytes = new Uint8Array(await file.arrayBuffer())
    // A protected backup cannot be opened until a password shows up; a plain
    // zip is a ledger and its files; a bare .sqlite (from before) is a ledger.
    if (isLocked(bytes)) locked.value = { name: file.name, bytes }
    else if (looksLikeZip(bytes)) await load(readBackup(await unzip(bytes)))
    else await load({ ledger: bytes }, 'sqlite')
  } catch (err) {
    toast(t('settings.readFailed', { reason: errorText(err) }), 'danger')
  } finally {
    busy.value = ''
  }
}

async function unlock() {
  restoreError.value = ''
  busy.value = 'restore'
  try {
    await load(readBackup(await unzipWithPassword(locked.value.bytes, password.value)))
    locked.value = null
    password.value = ''
  } catch (err) {
    restoreError.value = errorText(err)
  } finally {
    busy.value = ''
  }
}

/** Nothing of your own in it yet — no transaction, and nothing to file them under. */
const empty = computed(
  () =>
    transactionCount.value === 0 &&
    !state.tags.length &&
    !state.categories.length &&
    !state.plans.length &&
    !state.budgets.length &&
    !state.goals.length,
)

async function demo() {
  if (state.sync.on) return toast(t('sync.refused'), 'warn')
  busy.value = 'demo'
  await loadDemoData()
  await hydrate()
  busy.value = ''
  toast(t('settings.sampleLoaded'), 'good')
  closeSheet()
}

async function erase() {
  if (state.sync.on) return toast(t('sync.refused'), 'warn')
  if (!confirmErase.value) return (confirmErase.value = true)
  busy.value = 'erase'
  await eraseEverything()
  await hydrate()
  busy.value = ''
  confirmErase.value = false
  toast(t('settings.erased'))
}
</script>

<template>
  <Sheet large :title="t('header.settings')" :subtitle="t('settings.subtitle')" @close="closeSheet">
    <div class="grid gap-5 sm:grid-cols-[11rem_minmax(0,1fr)] sm:gap-6">
      <nav
        class="scroll-thin flex gap-0.5 overflow-x-auto sm:flex-col sm:gap-1 sm:self-start sm:overflow-visible"
        role="tablist"
        aria-orientation="vertical"
        @keydown="onTabKey"
      >
        <button
          v-for="(x, i) in TABS"
          :key="x.id"
          :id="`settings-tab-${x.id}`"
          class="flex flex-1 items-center justify-center gap-2 rounded-lg px-2 py-2 text-[13px] font-medium whitespace-nowrap transition sm:flex-none sm:justify-start sm:px-3 sm:text-left sm:text-[13.5px]"
          :class="tab === x.id ? 'bg-raised text-text' : 'text-muted hover:bg-raised/60 hover:text-text'"
          role="tab"
          :aria-selected="tab === x.id"
          :aria-controls="`settings-panel-${x.id}`"
          :tabindex="tab === x.id ? 0 : -1"
          :data-settings-tab="x.id"
          :aria-keyshortcuts="String(i + 1)"
          :data-autofocus="tab === x.id || undefined"
          @click="tab = x.id"
        >
          <Icon :name="x.icon" :size="16" class="hidden shrink-0 sm:block" :class="tab === x.id ? 'text-accent' : ''" />
          <span class="sm:flex-1">{{ t(x.label) }}</span>
          <!-- Not on a phone's row of tabs, which has no room for them (and no keys). -->
          <span class="hidden sm:inline-flex"><Kbd :combo="String(i + 1)" /></span>
        </button>
      </nav>

      <div class="min-w-0">
      <!-- General: how the money is counted and said. -->
      <div v-show="tab === 'general'" id="settings-panel-general" class="grid gap-5" role="tabpanel" aria-labelledby="settings-tab-general">
      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="label" for="st-currency">{{ t('settings.currency') }}</label>
          <select
            id="st-currency"
            class="input"
            :value="state.settings.currency"
            @change="setSetting('currency', $event.target.value)"
          >
            <option v-for="c in CURRENCIES" :key="c.code" :value="c.code">
              {{ c.code }} — {{ c.symbol }}
            </option>
          </select>
        </div>
        <div>
          <label class="label" for="st-language">{{ t('settings.language') }}</label>
          <select
            id="st-language"
            class="input"
            :value="language"
            @change="setLanguage($event.target.value)"
          >
            <!-- Each named in its own language: someone looking for their own
                 is not helped by seeing it written in one they cannot read. -->
            <option v-for="l in LANGUAGES" :key="l.code" :value="l.code">{{ l.label }}</option>
          </select>
        </div>

        <!-- The whole row: the hint under it needs the width. The theme is not
             here any more — the header's sun and moon switch it. -->
        <div class="col-span-2">
          <label class="label" for="st-bank">{{ t('settings.bank') }}</label>
          <select
            id="st-bank"
            class="input"
            :value="state.settings.bank ?? 'generic'"
            @change="setSetting('bank', $event.target.value)"
          >
            <option v-for="b in IMPORTERS" :key="b.id" :value="b.id">
              {{ t(`importer.${b.id}`) }}
            </option>
          </select>
          <p class="mt-1.5 text-[12px] text-faint">{{ t('settings.bankHint') }}</p>
        </div>

      </div>

      <div>
        <label class="label" for="st-open">{{ t('settings.opening') }}</label>
        <input id="st-open" v-model="opening" class="input tnum" inputmode="decimal" />
        <p class="mt-1.5 text-[12px] text-faint">{{ t('settings.openingHint') }}</p>
      </div>

      <div>
        <label class="label" for="st-balance">{{ t('settings.currentBalance') }}</label>
        <input
          id="st-balance"
          class="input tnum"
          inputmode="decimal"
          :value="balanceField"
          @change="matchBalance"
        />
        <p class="mt-1.5 text-[12px] text-faint">
          {{ t('settings.currentBalanceHint') }}
        </p>
      </div>

      <label class="flex items-start gap-3 rounded-xl border border-line-soft bg-raised/50 p-3.5">
        <input
          type="checkbox"
          class="mt-0.5 size-4 accent-[var(--c-accent)]"
          :checked="state.settings.countUpcomingIncome"
          @change="setSetting('countUpcomingIncome', $event.target.checked)"
        />
        <span>
          <span class="block text-[13.5px] font-medium">{{ t('settings.countUpcoming') }}</span>
          <span class="mt-0.5 block text-[12.5px] text-muted">
            {{ t('settings.countUpcomingHint') }}
          </span>
        </span>
      </label>


      </div>

      <!-- Suggestions: the model that guesses what a transaction is. -->
      <div
        v-if="opened.has('suggestions')"
        v-show="tab === 'suggestions'"
        id="settings-panel-suggestions"
        role="tabpanel"
        aria-labelledby="settings-tab-suggestions"
      >
        <SuggestionsPanel />
      </div>

      <!-- Usage: what OKANE is used for, for an admin. -->
      <div
        v-if="admin && opened.has('usage')"
        v-show="tab === 'usage'"
        id="settings-panel-usage"
        role="tabpanel"
        aria-labelledby="settings-tab-usage"
      >
        <UsagePanel />
      </div>

      <!-- Sync: where the ledger lives — this device, or the OKANE server. -->
      <div
        v-if="opened.has('sync')"
        v-show="tab === 'sync'"
        id="settings-panel-sync"
        role="tabpanel"
        aria-labelledby="settings-tab-sync"
        data-settings-cloud
      >
        <!-- Where the ledger is, and moving it: this device or the cloud, readable or encrypted. -->
        <LedgerPicker class="mb-6" />
        <LedgerStates :next="payload?.next" @goto="tab = $event" />
        <div class="mt-8 grid gap-8 border-t border-line-soft pt-6">
          <!-- An encrypted org: how its sync stands. -->
          <SyncPanel v-if="state.sync.on" :approve="payload?.approve" />
          <div>
            <p v-if="state.cloud.account && !state.sync.on" class="mb-4 text-[12.5px] text-faint">
              {{ t('settings.cloudHintCloud', { email: state.cloud.account.user.email, org: state.cloud.org?.name ?? '' }) }}
            </p>
            <p v-else-if="!state.cloud.account" class="mb-4 text-[12.5px] text-faint">{{ t('settings.cloudHintLocal') }}</p>
            <CloudPanel @goto="tab = $event" />
          </div>
        </div>
      </div>

      <!-- Your data: where it is kept, and backing it up, restoring or erasing it. -->
      <div
        v-if="opened.has('data')"
        v-show="tab === 'data'"
        id="settings-panel-data"
        role="tabpanel"
        aria-labelledby="settings-tab-data"
      >
        <p class="mb-3 text-[12.5px] text-faint">
          {{ t(state.cloud.account ? 'settings.yourDataHintCloud' : 'settings.yourDataHint') }}
        </p>
        <!-- Where exactly, and whether the browser has promised to keep it. The
             unprotected case is the one worth reading: it is how data goes. -->
        <div v-if="state.storage.backend" class="mb-3 grid gap-1 text-[12.5px]" data-storage>
          <p class="flex items-start gap-1.5 text-muted">
            <Icon name="folder" :size="14" class="mt-0.5 shrink-0" />
            {{ t(`settings.storage.${state.storage.backend}`) }}
          </p>
          <p
            v-if="state.storage.persisted !== null"
            class="flex items-start gap-1.5"
            :class="state.storage.persisted ? 'text-muted' : 'text-warn'"
            :data-persisted="state.storage.persisted"
          >
            <Icon :name="state.storage.persisted ? 'check' : 'alert'" :size="14" class="mt-0.5 shrink-0" />
            {{ t(state.storage.persisted ? 'settings.storage.protected' : 'settings.storage.unprotected') }}
          </p>
        </div>
        <!-- What the attached files take, and, in the cloud, may take. -->
        <p v-if="filesUsed" class="mb-3 flex items-start gap-1.5 text-[12.5px] text-muted" data-files-usage>
          <Icon name="clip" :size="14" class="mt-0.5 shrink-0" />
          {{
            filesUsed.limit
              ? t('settings.filesUsedOf', { used: sizeLabel(filesUsed.used, state.settings.language), limit: sizeLabel(filesUsed.limit, state.settings.language) })
              : t('settings.filesUsed', { used: sizeLabel(filesUsed.used, state.settings.language) })
          }}
        </p>
        <div v-if="locked" class="mb-2 rounded-xl border border-line-soft bg-raised/50 p-3">
          <p class="mb-2 truncate text-[12.5px] text-muted">
            <Icon name="upload" :size="14" class="mr-1 inline" />{{ t('settings.lockedFile', { name: locked.name }) }}
          </p>
          <div class="flex gap-2">
            <input
              v-model="password"
              type="password"
              class="input"
              :placeholder="t('settings.itsPassword')"
              autocomplete="off"
              @keydown.enter.prevent="unlock"
            />
            <button class="btn-primary shrink-0" :disabled="!password || busy === 'restore'" @click="unlock">
              {{ busy === 'restore' ? t('settings.openingFile') : t('settings.restore') }}
            </button>
            <button class="btn-ghost shrink-0" @click="locked = null; password = ''; restoreError = ''">
              {{ t('common.cancel') }}
            </button>
          </div>
          <p v-if="restoreError" class="mt-2 text-[12.5px] text-danger">{{ restoreError }}</p>
        </div>

        <!-- Backing up, right here: with a password, or without. -->
        <section class="mb-5 rounded-xl border border-line-soft p-4" data-backup-box>
          <p class="mb-3 text-[13.5px] font-semibold">{{ t('backup.title') }}</p>
          <BackupPanel />
        </section>

        <div class="grid grid-cols-2 gap-2">
          <button class="btn-soft" :disabled="busy === 'restore'" @click="fileInput.click()">
            <Icon name="upload" :size="16" /> {{ t('settings.restore') }}
          </button>
          <input
            ref="fileInput"
            type="file"
            accept=".sqlite,.db,.zip,application/x-sqlite3,application/zip"
            class="hidden"
            @change="restore"
          />
          <!-- Sample data is for looking around an empty OKANE: loaded on top of
               a real ledger it would only muddle it. Import lives in the header. -->
          <button v-if="empty" class="btn-soft" :disabled="busy === 'demo'" @click="demo">
            <Icon name="spark" :size="16" /> {{ busy === 'demo' ? t('settings.loading') : t('settings.sampleData') }}
          </button>
          <button class="btn-danger" :disabled="busy === 'erase'" @click="erase">
            <Icon name="trash" :size="16" /> {{ confirmErase ? t('common.confirmDelete') : t('settings.eraseAll') }}
          </button>
        </div>

        <p class="mt-5 text-center text-[11.5px] text-faint">
          {{
            t('settings.footer', {
              transactions: transactionCount,
              plans: state.plans.length,
              goals: state.goals.length,
            })
          }}
        </p>
      </div>
      </div>
    </div>

    <template #footer>
      <div class="flex-1" />
      <button data-primary :aria-keyshortcuts="ariaCombo('mod+enter')" class="btn-primary" @click="closeSheet">{{ t('common.done') }}<Kbd combo="mod+enter" /></button>
    </template>
  </Sheet>
</template>
